import React from 'react';
import { ContactProvider, useContact } from './context/ContactContext';
import { TranscriptProvider } from './context/TranscriptContext';
import LeftRail from './components/LeftRail/LeftRail';
import CentrePanel from './components/CentrePanel/CentrePanel';
import WaitingForContact from './components/CentrePanel/WaitingForContact';
import TaskPanel from './components/CentrePanel/TaskPanel';
import ChatPanel from './components/CentrePanel/ChatPanel';
import IncomingCall from './components/CentrePanel/IncomingCall';
import RightRail from './components/RightRail/RightRail';
import LoggedOut from './components/LoggedOut/LoggedOut';
import WrapUp from './components/CentrePanel/WrapUp';
import LoginGate from './components/LoggedOut/LoginGate';
import SoftphoneBanner from './components/SoftphoneBanner';
import AwayGuard from './components/AwayGuard';
import NewVersionStrip from './components/NewVersionStrip';
import ConsoleHeader from './components/Header/ConsoleHeader';
import { useConsoleAuth } from './auth/useConsoleAuth';

function Shell() {
  const { ccpContainerRef, agentConnected, display, incomingContact, acwActive, activeTab, wrapUpBlocked } = useContact();

  // A live contact always seizes the centre, whatever tab is selected.
  // Tabs only choose the idle view: voice waiting / chat placeholder / task queue.
  const idleCentre = activeTab === 'task'
    ? <TaskPanel />
    : activeTab === 'chat'
      ? <ChatPanel />
      : <WaitingForContact />;

  // #113 (2026-09-22): the v3 header sits above every post-sign-in screen,
  // carrying the wordmark, the stats shell and identity + sign-out. The
  // ringing screen now renders INSIDE the shell (header, rails, card in the
  // centre) instead of replacing it, as the spec draws it.
  return (
    <div className="ccp-shell v3-shell">
      {/* Incident 2026-09-14 fix 2: softphone/CCP errors, fixed strip at the top */}
      <SoftphoneBanner />
      {/* Hidden iframe — Amazon Connect Streams embeds the CCP here */}
      <div ref={ccpContainerRef} className="ccp-hidden" aria-hidden="true" />
      {/* Backlog 205: a newer console is live; refresh between calls */}
      <NewVersionStrip />
      {/* Backlog 109: away means not Available — idle prompt and missed-call notice */}
      <AwayGuard />
      <ConsoleHeader />
      <div className="ccp-body">
        {!agentConnected ? (
          <LoggedOut />
        ) : incomingContact ? (
          <>
            <LeftRail />
            <IncomingCall />
            <RightRail />
          </>
        ) : acwActive && wrapUpBlocked ? (
          // Row 94: a wrap-up is waiting but a call or chat is still live. The
          // live contact keeps the screen; the wrap-up form opens when it ends.
          <>
            <LeftRail />
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <div role="status" style={{ margin: '16px 24px 0', padding: '12px 16px', border: '1px solid rgba(240,180,90,0.45)', borderRadius: 8, background: 'rgba(240,180,90,0.08)', color: '#f0c98a', fontSize: 14 }}>
                A wrap-up is waiting. {wrapUpBlocked} The wrap-up form opens as soon as it ends.
              </div>
              {display ? <CentrePanel /> : idleCentre}
            </div>
            <RightRail />
          </>
        ) : acwActive ? (
          // ACW gate: the wrap-up must be saved before the contact completes.
          // Left rail stays for status/controls; centre is the wrap-up form.
          <>
            <LeftRail />
            <WrapUp />
          </>
        ) : (
          <>
            <LeftRail />
            {display ? <CentrePanel /> : idleCentre}
            <RightRail />
          </>
        )}
      </div>
    </div>
  );
}

export default function App() {
  // D11-c: console login gate. Nothing — not even the hidden CCP iframe —
  // mounts until there is a Cognito session, so no API call can go out
  // without a token.
  const auth = useConsoleAuth();
  if (!auth.ready) return <LoginGate busy />;
  if (!auth.session) return <LoginGate error={auth.error} />;
  return (
    <ContactProvider>
      {/* Phase 3: one Contact Lens poller for transcript + sentiment pills */}
      <TranscriptProvider>
        <Shell />
      </TranscriptProvider>
    </ContactProvider>
  );
}
